import type { ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import UptimeLatencyBadge from "@/v2-backoffice-client/uptime/UptimeLatencyBadge"
import {
  classifyLatency,
  formatLatency,
} from "@/v2-backoffice-client/uptime/uptime.utils"
import type { UptimeTargetFragment } from "@/v2-backoffice-client/uptime/__types__/uptime.fragment"

export type UptimeViewLatencyStatsProps = ComponentProps<"div"> & {
  target: UptimeTargetFragment
}

// Five latency tiles: current (24h avg), the 7d percentile
// distribution (p50 / p95 / p99; the fields keep their legacy *90d
// suffix for API compatibility), and the true 90d mean. The 24h avg
// carries a baseline-aware colour so the "slow outlier" state is
// immediately visible.
function UptimeViewLatencyStats(props: UptimeViewLatencyStatsProps) {
  const { target, className, ...divProps } = props

  const baseline = target.P95DurationMs90d ?? target.AvgDurationMs90d ?? null

  const tiles: Array<{
    label: string
    value: number | null | undefined
    klass?: ReturnType<typeof classifyLatency>
    hint?: string
  }> = [
    {
      label: "24 h avg",
      value: target.AvgDurationMs24h,
      klass: classifyLatency(target.AvgDurationMs24h, baseline),
      hint:
        target.IsSlowOutlier && baseline != null
          ? `Slow outlier: well above the long-term baseline`
          : "Rolling 24h mean of successful probes",
    },
    {
      label: "p50 · 7 d",
      value: target.P50DurationMs90d,
      klass: "normal",
      hint: "Median response time over the last 7 days",
    },
    {
      label: "avg · 90 d",
      value: target.AvgDurationMs90d,
      klass: "normal",
      hint: "Arithmetic mean over 90 days",
    },
    {
      label: "p95 · 7 d",
      value: target.P95DurationMs90d,
      klass: "normal",
      hint: "95% of probes over the last 7 days completed within this time",
    },
    {
      label: "p99 · 7 d",
      value: target.P99DurationMs90d,
      klass: "normal",
      hint: "99% of probes over the last 7 days completed within this time",
    },
  ]

  return (
    <div
      className={cn("uptime-view-chart-frame flex flex-col gap-4", className)}
      {...divProps}
    >
      <div className="font-medium">Response time percentiles</div>
      <div className="uptime-view-latency-grid">
        {tiles.map((t) => (
          <div
            key={t.label}
            className="uptime-view-latency-tile"
            title={t.hint}
          >
            <div className="uptime-view-latency-tile-label">{t.label}</div>
            {t.value != null ? (
              <UptimeLatencyBadge
                currentMs={t.value}
                variant={t.klass}
                tooltip={t.hint ?? formatLatency(t.value)}
              />
            ) : (
              <span className="uptime-view-latency-tile-empty">—</span>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

export default UptimeViewLatencyStats
